import { useState, useEffect, useMemo, useCallback } from "react";
import {
  adminLogin,
  adminLogout,
  adminFetchTickets,
  adminFetchTicketDetail,
  adminDownloadTicketAttachment,
  adminUpdateTicket,
  getAdminToken,
} from "../api/serviceDeskApi";

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

function formatStatus(s) {
  const o = STATUS_OPTIONS.find((x) => x.value === s);
  return o ? o.label : s;
}

function statusBadgeClass(s) {
  const map = {
    open: "sd-admin-badge sd-admin-badge--open",
    in_progress: "sd-admin-badge sd-admin-badge--progress",
    resolved: "sd-admin-badge sd-admin-badge--resolved",
    closed: "sd-admin-badge sd-admin-badge--closed",
  };
  return map[s] || "sd-admin-badge sd-admin-badge--muted";
}

export default function ServiceDeskAdminPage({ embedded = false }) {
  const [authed, setAuthed] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [listError, setListError] = useState("");
  const [actionBanner, setActionBanner] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [detailById, setDetailById] = useState({});
  const [attachmentLoadingId, setAttachmentLoadingId] = useState(null);

  const [viewModalId, setViewModalId] = useState(null);
  const [updateModalId, setUpdateModalId] = useState(null);
  const [updateDraftStatus, setUpdateDraftStatus] = useState("");
  const [updateDraftNote, setUpdateDraftNote] = useState("");
  const [updateSubmitting, setUpdateSubmitting] = useState(false);

  const anyModalOpen = Boolean(viewModalId || updateModalId);

  const closeModals = useCallback(() => {
    setViewModalId(null);
    setUpdateModalId(null);
    setUpdateDraftNote("");
    setUpdateSubmitting(false);
  }, []);

  const stats = useMemo(() => {
    const o = { total: tickets.length, open: 0, inProgress: 0, done: 0 };
    tickets.forEach((t) => {
      if (t.status === "open") o.open += 1;
      else if (t.status === "in_progress") o.inProgress += 1;
      else if (t.status === "resolved" || t.status === "closed") o.done += 1;
    });
    return o;
  }, [tickets]);

  useEffect(() => {
    if (!actionBanner) return undefined;
    const t = setTimeout(() => setActionBanner(null), 8000);
    return () => clearTimeout(t);
  }, [actionBanner]);

  useEffect(() => {
    if (!anyModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeModals();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [anyModalOpen, closeModals]);

  useEffect(() => {
    if (anyModalOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [anyModalOpen]);

  const refresh = async () => {
    setListError("");
    try {
      const list = await adminFetchTickets();
      setTickets(list);
    } catch (e) {
      const msg = e?.message || "Could not load tickets";
      setListError(msg);
      setTickets([]);
      if (msg === "Not logged in" || msg.includes("Session expired") || msg.includes("log in again")) {
        adminLogout();
        setAuthed(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getAdminToken()) {
        setAuthed(false);
        setBootstrapping(false);
        return;
      }
      try {
        const list = await adminFetchTickets();
        if (!cancelled) {
          setTickets(list);
          setAuthed(true);
        }
      } catch {
        if (!cancelled) {
          adminLogout();
          setAuthed(false);
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!viewModalId || !authed) return undefined;
    let cancelled = false;
    adminFetchTicketDetail(viewModalId)
      .then((full) => {
        if (!cancelled) setDetailById((d) => ({ ...d, [viewModalId]: full }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [viewModalId, authed]);

  const filtered = useMemo(() => {
    if (filter === "all") return tickets;
    return tickets.filter((t) => t.status === filter);
  }, [tickets, filter]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await adminLogin(username.trim(), password);
      setPassword("");
      const list = await adminFetchTickets();
      setTickets(list);
      setListError("");
      setAuthed(true);
    } catch (err) {
      adminLogout();
      setAuthed(false);
      setTickets([]);
      setLoginError(err?.message || "Login failed");
    }
  };

  const logout = () => {
    closeModals();
    adminLogout();
    setAuthed(false);
    setTickets([]);
    setActionBanner(null);
  };

  const openView = (id) => {
    setUpdateModalId(null);
    setViewModalId(id);
  };

  const openUpdate = (id) => {
    const tick = tickets.find((x) => x.id === id);
    setViewModalId(null);
    setUpdateModalId(id);
    setUpdateDraftStatus(tick?.status || "open");
    setUpdateDraftNote("");
  };

  const downloadAttachment = async (ticketId) => {
    setAttachmentLoadingId(ticketId);
    setListError("");
    try {
      const { blob, fileName } = await adminDownloadTicketAttachment(ticketId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.message || "Could not download attachment";
      setListError(msg);
      if (msg.includes("Session expired") || msg.includes("log in again")) {
        adminLogout();
        setAuthed(false);
      }
    } finally {
      setAttachmentLoadingId(null);
    }
  };

  const submitModalUpdate = async () => {
    const id = updateModalId;
    if (!id || !updateDraftStatus) return;
    const note = updateDraftNote.trim();
    const prevStatus = tickets.find((te) => te.id === id)?.status;
    const statusChanged = prevStatus !== updateDraftStatus;
    setActionBanner(null);
    setUpdateSubmitting(true);
    setListError("");
    try {
      const { customerNotified } = await adminUpdateTicket(id, updateDraftStatus, note);
      await refresh();
      closeModals();
      if (statusChanged) {
        if (customerNotified) {
          setActionBanner({ type: "ok", text: "Status saved — notification email was sent to the customer." });
        } else {
          setActionBanner({
            type: "warn",
            text: "Status saved. Email was not sent — add smtp_host in api/config.php (or fix PHP mail), and check spam.",
          });
        }
      } else {
        setActionBanner({ type: "info", text: "Saved. Status was unchanged — no email is sent for duplicate status." });
      }
      if (detailById[id]) {
        try {
          const full = await adminFetchTicketDetail(id);
          setDetailById((d) => ({ ...d, [id]: full }));
        } catch (_) {}
      }
    } catch (e) {
      setListError(e?.message || "Update failed");
    } finally {
      setUpdateSubmitting(false);
    }
  };

  const viewTicket = viewModalId ? tickets.find((x) => x.id === viewModalId) : null;
  const viewDetail = viewModalId ? (detailById[viewModalId] || viewTicket) : null;
  const updateTicket = updateModalId ? tickets.find((x) => x.id === updateModalId) : null;

  if (bootstrapping) {
    return (
      <section className={`sd-admin sd-admin--loading${embedded ? " sd-admin--embedded" : ""} section section-light d-flex align-items-center justify-content-center`} style={{ minHeight: embedded ? "40vh" : "55vh" }}>
        <div className="sd-admin-spinner-wrap text-center">
          <div className="sd-admin-spinner mb-3" aria-hidden="true" />
          <div className="text-muted small">Loading dashboard…</div>
        </div>
      </section>
    );
  }

  if (!authed) {
    if (embedded) {
      return (
        <div className="admin-page sd-admin--embedded">
          <header className="admin-page-head">
            <div>
              <h1>Service Desk</h1>
              <p>Sign in to the ticket API to review and update complaints.</p>
            </div>
          </header>
          <div className="admin-panel" style={{ maxWidth: 480 }}>
            <h2 className="h5 mb-1">API Sign in</h2>
            <p className="small text-muted mb-3">
              Sign in with your admin username and password.
            </p>
            {loginError && <div className="alert alert-danger py-2 small mb-3">{loginError}</div>}
            <form onSubmit={handleLogin}>
              <label className="form-label small fw-semibold">Username</label>
              <input className="form-control mb-3" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} />
              <label className="form-label small fw-semibold">Password</label>
              <input type="password" className="form-control mb-3" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="submit" className="admin-btn admin-btn-primary">Enter Service Desk</button>
            </form>
          </div>
        </div>
      );
    }
    return (
      <div className="sd-admin">
        <div className="sd-admin-login-hero">
          <div className="container py-5">
            <div className="row align-items-center g-4">
              <div className="col-lg-6 text-lg-start text-center">
                <div className="sd-admin-login-badge mb-3">Admin</div>
                <h1 className="sd-admin-login-title">Service Desk</h1>
                <p className="sd-admin-login-lead mb-0">Review complaints, update status, and keep customers informed.</p>
              </div>
              <div className="col-lg-5 ms-lg-auto">
                <div className="sd-admin-login-card contact-form-card shadow-lg border-0">
                  <h2 className="h5 mb-1">Sign in</h2>
                  <p className="small text-muted mb-4">
                    Sign in with your admin username and password.
                  </p>
                  {loginError && <div className="alert alert-danger py-2 small mb-3">{loginError}</div>}
                  <form onSubmit={handleLogin}>
                    <label className="form-label small fw-semibold">Username</label>
                    <input className="form-control mb-3" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} />
                    <label className="form-label small fw-semibold">Password</label>
                    <input type="password" className="form-control mb-4" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                    <button type="submit" className="btn-primary-custom w-100">Enter dashboard</button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (embedded) {
    return (
      <div className="admin-page sd-admin sd-admin--embedded">
        <header className="admin-page-head">
          <div>
            <h1>Service Desk</h1>
            <p>Tickets from the public Service Desk form. Customers track with ticket ID.</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button type="button" className="admin-btn admin-btn-ghost" onClick={refresh}>
              <i className="bi bi-arrow-clockwise" /> Refresh
            </button>
            <button type="button" className="admin-btn admin-btn-sm admin-btn-danger" onClick={logout}>
              API Logout
            </button>
          </div>
        </header>

        <div className="admin-stat-grid mb-3">
          <div className="admin-stat-card"><span>Total</span><strong>{stats.total}</strong></div>
          <div className="admin-stat-card"><span>Open</span><strong>{stats.open}</strong></div>
          <div className="admin-stat-card"><span>In progress</span><strong>{stats.inProgress}</strong></div>
          <div className="admin-stat-card"><span>Resolved / closed</span><strong>{stats.done}</strong></div>
        </div>

        {listError ? <div className="alert alert-warning py-2 small">{listError}</div> : null}
        {actionBanner ? (
          <div className={`alert py-2 px-3 mb-3 small ${actionBanner.type === "success" ? "alert-success" : "alert-info"}`}>
            {actionBanner.message}
          </div>
        ) : null}

        <div className="admin-panel mb-3">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <span className="text-muted small text-uppercase fw-bold me-1">Filter</span>
            {["all", "open", "in_progress", "resolved", "closed"].map((f) => (
              <button
                key={f}
                type="button"
                className={`admin-btn admin-btn-sm${filter === f ? " admin-btn-primary" : " admin-btn-ghost"}`}
                onClick={() => setFilter(f)}
              >
                {f === "all" ? "All" : formatStatus(f)}
              </button>
            ))}
          </div>
        </div>

        <div className="admin-panel">
          {filtered.length === 0 ? (
            <p className="admin-empty">No tickets yet. Submit one from the public Service Desk page.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Customer</th>
                    <th>Company</th>
                    <th>Status</th>
                    <th>Team</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <strong className="font-monospace">{t.id}</strong>
                        <div className="text-muted small">{t.createdAt ? new Date(t.createdAt).toLocaleString() : "—"}</div>
                      </td>
                      <td>
                        <div>{t.fullName}</div>
                        <div className="text-muted small">{t.email}</div>
                      </td>
                      <td>{t.company}</td>
                      <td><span className={statusBadgeClass(t.status)}>{formatStatus(t.status)}</span></td>
                      <td>{t.routedTeam}</td>
                      <td className="admin-actions">
                        <button type="button" className="admin-btn admin-btn-sm" onClick={() => openView(t.id)}>View</button>
                        <button type="button" className="admin-btn admin-btn-sm admin-btn-primary" onClick={() => openUpdate(t.id)}>Update</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* reuse existing modals by rendering the rest of original modal JSX - keep below */}
        {anyModalOpen ? (
          <div className="sd-admin-modal-root" role="presentation">
            <button type="button" className="sd-admin-modal-backdrop" aria-label="Close" onClick={closeModals} />
          </div>
        ) : null}

        {viewModalId && viewDetail ? (
          <div className="modal fade show d-block sd-admin-modal" tabIndex={-1} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content sd-admin-modal-content border-0 shadow">
                <div className="modal-header sd-admin-modal-header border-0 pb-0">
                  <div>
                    <h2 className="modal-title h5 mb-1">Ticket details</h2>
                    <div className="font-monospace small text-muted">{viewDetail.id}</div>
                  </div>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeModals} />
                </div>
                <div className="modal-body">
                  <div className="row g-3 small mb-3">
                    <div className="col-sm-6 col-lg-4">
                      <div className="sd-admin-detail-k">Customer</div>
                      <div className="sd-admin-detail-v">{viewDetail.fullName}</div>
                      <div className="text-muted small text-break">{viewDetail.email}</div>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <div className="sd-admin-detail-k">Company</div>
                      <div className="sd-admin-detail-v">{viewDetail.company}</div>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <div className="sd-admin-detail-k">Status</div>
                      <div><span className={statusBadgeClass(viewDetail.status)}>{formatStatus(viewDetail.status)}</span></div>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <div className="sd-admin-detail-k">Team / priority</div>
                      <div className="sd-admin-detail-v">{viewDetail.routedTeam}</div>
                      {viewDetail.effectivePriority ? (
                        <span className={`service-desk-pill service-desk-pill--${String(viewDetail.effectivePriority).toLowerCase()}`}>{viewDetail.effectivePriority}</span>
                      ) : null}
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <div className="sd-admin-detail-k">Issue type</div>
                      <div className="sd-admin-detail-v">{viewDetail.issueType || "—"}</div>
                    </div>
                    {viewDetail.phone ? (
                      <div className="col-sm-6 col-lg-4">
                        <div className="sd-admin-detail-k">Phone</div>
                        <div className="sd-admin-detail-v">{viewDetail.phone}</div>
                      </div>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <div className="sd-admin-detail-k">Title</div>
                    <div className="fw-semibold">{viewDetail.issueTitle}</div>
                  </div>
                  <div className="mb-3">
                    <div className="sd-admin-detail-k">Description</div>
                    <div className="text-muted small">{viewDetail.issueDescription}</div>
                  </div>
                  {viewDetail.attachment ? (
                    <div className="mb-3 pt-2 border-top">
                      <div className="sd-admin-detail-k">Attachment</div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={attachmentLoadingId === viewDetail.id}
                        onClick={() => downloadAttachment(viewDetail.id)}
                      >
                        {attachmentLoadingId === viewDetail.id ? "Downloading…" : viewDetail.attachment.fileName}
                      </button>
                    </div>
                  ) : null}
                  {viewDetail.statusLog?.length > 0 ? (
                    <div className="sd-admin-timeline-wrap">
                      <div className="sd-admin-detail-k mb-2">Activity</div>
                      <ul className="list-unstyled mb-0 small sd-admin-timeline">
                        {[...viewDetail.statusLog].reverse().map((log, i) => (
                          <li key={i} className="sd-admin-timeline-item">
                            <span className="text-muted">{new Date(log.at).toLocaleString()}</span>
                            {" · "}
                            <strong>{formatStatus(log.status)}</strong>
                            {log.note ? ` — ${log.note}` : ""}
                            <span className="text-muted"> ({log.by})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
                <div className="modal-footer border-0">
                  <button type="button" className="btn btn-light" onClick={closeModals}>Close</button>
                  <button type="button" className="btn sd-admin-save-btn" onClick={() => { closeModals(); openUpdate(viewModalId); }}>Update status</button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {updateModalId && updateTicket ? (
          <div className="modal fade show d-block sd-admin-modal" tabIndex={-1} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content sd-admin-modal-content border-0 shadow">
                <div className="modal-header border-0 pb-0">
                  <div>
                    <h2 className="modal-title h5 mb-1">Update status</h2>
                    <div className="font-monospace small text-muted">{updateTicket.id}</div>
                  </div>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeModals} />
                </div>
                <div className="modal-body">
                  <label className="form-label fw-semibold small">Status</label>
                  <select className="form-select mb-3" value={updateDraftStatus} onChange={(e) => setUpdateDraftStatus(e.target.value)}>
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <label className="form-label fw-semibold small">Note (optional)</label>
                  <textarea className="form-control" rows={3} value={updateDraftNote} onChange={(e) => setUpdateDraftNote(e.target.value)} />
                </div>
                <div className="modal-footer border-0">
                  <button type="button" className="btn btn-light" onClick={closeModals}>Cancel</button>
                  <button type="button" className="btn sd-admin-save-btn" onClick={submitModalUpdate} disabled={updateSubmitting || !updateDraftStatus}>
                    {updateSubmitting ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="sd-admin">
      <div className="sd-admin-hero">
        <div className="container-fluid px-3 px-lg-4 py-4 py-lg-5">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <div className="sd-admin-hero-badge mb-2">Operations</div>
              <h1 className="sd-admin-hero-title h2 mb-2">Service Desk — complaints</h1>
              <p className="sd-admin-hero-sub text-white-50 small mb-0" style={{ maxWidth: 520 }}>
                Tickets from your site (<code className="text-white-50">emicron</code> DB). Customers track with their ticket ID on the public Service Desk page.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <button type="button" className="btn sd-admin-btn-light btn-sm" onClick={refresh}>
                <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" /> Refresh
              </button>
              <button type="button" className="btn sd-admin-btn-outline btn-sm" onClick={logout}>
                <i className="bi bi-box-arrow-right me-1" aria-hidden="true" /> Log out
              </button>
            </div>
          </div>

          <div className="row g-3 mt-2 mt-lg-4">
            <div className="col-6 col-md-3">
              <div className="sd-admin-stat">
                <div className="sd-admin-stat-value">{stats.total}</div>
                <div className="sd-admin-stat-label">Total tickets</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="sd-admin-stat">
                <div className="sd-admin-stat-value sd-admin-stat-value--open">{stats.open}</div>
                <div className="sd-admin-stat-label">Open</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="sd-admin-stat">
                <div className="sd-admin-stat-value sd-admin-stat-value--progress">{stats.inProgress}</div>
                <div className="sd-admin-stat-label">In progress</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="sd-admin-stat">
                <div className="sd-admin-stat-value sd-admin-stat-value--done">{stats.done}</div>
                <div className="sd-admin-stat-label">Resolved / closed</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="section section-light sd-admin-body pb-5">
        <div className="container-fluid px-3 px-lg-4">
          {actionBanner && (
            <div
              className={`alert py-2 px-3 mb-3 small sd-admin-flash border-0 ${
                actionBanner.type === "ok" ? "alert-success" : actionBanner.type === "info" ? "alert-secondary" : "alert-warning"
              }`}
              role="status"
            >
              {actionBanner.text}
            </div>
          )}
          {listError && <div className="alert alert-danger py-2 mb-3 small">{listError}</div>}

          <div className="sd-admin-toolbar mb-4">
            <span className="sd-admin-toolbar-label text-muted small text-uppercase fw-bold me-2">Filter</span>
            <div className="btn-group flex-wrap" role="group">
              <button type="button" className={`btn btn-sm ${filter === "all" ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => setFilter("all")}>All</button>
              {STATUS_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`btn btn-sm ${filter === o.value ? "btn-dark" : "btn-outline-secondary"}`}
                  onClick={() => setFilter(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {tickets.length === 0 ? (
            <div className="sd-admin-empty alert alert-info border-0 shadow-sm">No tickets yet. Submit one from the public Service Desk page.</div>
          ) : (
            <div className="sd-admin-grid-wrap">
              <div className="table-responsive sd-admin-table-responsive">
                <table className="sd-admin-fancy-table w-100">
                  <thead>
                    <tr>
                      <th scope="col">Ticket</th>
                      <th scope="col">Created</th>
                      <th scope="col">Customer</th>
                      <th scope="col">Company</th>
                      <th scope="col">Team</th>
                      <th scope="col">Priority</th>
                      <th scope="col">Status</th>
                      <th scope="col">File</th>
                      <th scope="col" className="text-end sd-admin-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((t) => (
                      <tr key={t.id} className="sd-admin-data-row">
                        <td data-label="Ticket">
                          <div className="sd-admin-ticket-cell">
                            <span className="sd-admin-ticket-code font-monospace">{t.id}</span>
                          </div>
                        </td>
                        <td data-label="Created">
                          <span className="sd-admin-meta">{t.createdAt ? new Date(t.createdAt).toLocaleString() : "—"}</span>
                        </td>
                        <td data-label="Customer">
                          <div className="sd-admin-customer">
                            <span className="sd-admin-customer-name">{t.fullName}</span>
                            <span className="sd-admin-customer-email text-break">{t.email}</span>
                          </div>
                        </td>
                        <td data-label="Company"><span className="sd-admin-meta">{t.company}</span></td>
                        <td data-label="Team"><span className="sd-admin-team">{t.routedTeam}</span></td>
                        <td data-label="Priority">
                          <span className={`service-desk-pill service-desk-pill--${String(t.effectivePriority).toLowerCase()}`}>{t.effectivePriority}</span>
                        </td>
                        <td data-label="Status"><span className={statusBadgeClass(t.status)}>{formatStatus(t.status)}</span></td>
                        <td data-label="File">
                          {t.attachment ? (
                            <span className="sd-admin-file-pill" title={t.attachment.fileName}>
                              <i className="bi bi-paperclip" aria-hidden="true" />
                              <span className="text-truncate d-inline-block align-bottom sd-admin-file-name">{t.attachment.fileName}</span>
                            </span>
                          ) : (
                            <span className="text-muted sd-admin-dash">—</span>
                          )}
                        </td>
                        <td data-label="Actions" className="sd-admin-actions-cell text-end">
                          <div className="d-flex flex-wrap gap-2 justify-content-end">
                            <button type="button" className="btn btn-sm sd-admin-btn-view" onClick={() => openView(t.id)}>
                              <i className="bi bi-eye me-1" aria-hidden="true" /> View
                            </button>
                            <button type="button" className="btn btn-sm sd-admin-btn-update" onClick={() => openUpdate(t.id)}>
                              <i className="bi bi-arrow-repeat me-1" aria-hidden="true" /> Update
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {anyModalOpen && (
        <div className="sd-admin-modal-root" role="presentation">
          <button type="button" className="sd-admin-modal-backdrop" aria-label="Close" onClick={closeModals} />
        </div>
      )}

      {viewModalId && viewTicket && (
        <div className="modal fade show d-block sd-admin-modal" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="sd-admin-view-title">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content sd-admin-modal-content border-0 shadow">
              <div className="modal-header sd-admin-modal-header border-0 pb-0">
                <div>
                  <h2 className="modal-title h5 mb-1" id="sd-admin-view-title">Ticket details</h2>
                  <p className="mb-0 small text-muted font-monospace">{viewTicket.id}</p>
                </div>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeModals} />
              </div>
              <div className="modal-body pt-3">
                {viewDetail && (
                  <>
                    <div className="row g-3 small mb-3">
                      <div className="col-sm-6 col-lg-4">
                        <div className="sd-admin-detail-k">Customer</div>
                        <div className="sd-admin-detail-v">{viewDetail.fullName}</div>
                        <div className="text-muted small text-break">{viewDetail.email}</div>
                      </div>
                      <div className="col-sm-6 col-lg-4">
                        <div className="sd-admin-detail-k">Company</div>
                        <div className="sd-admin-detail-v">{viewDetail.company}</div>
                      </div>
                      <div className="col-sm-6 col-lg-4">
                        <div className="sd-admin-detail-k">Status</div>
                        <div><span className={statusBadgeClass(viewDetail.status)}>{formatStatus(viewDetail.status)}</span></div>
                      </div>
                      <div className="col-sm-6 col-lg-4">
                        <div className="sd-admin-detail-k">Team / priority</div>
                        <div className="sd-admin-detail-v">{viewDetail.routedTeam}</div>
                        <span className={`service-desk-pill service-desk-pill--${String(viewDetail.effectivePriority).toLowerCase()}`}>{viewDetail.effectivePriority}</span>
                      </div>
                      <div className="col-sm-6 col-lg-4">
                        <div className="sd-admin-detail-k">Issue type</div>
                        <div className="sd-admin-detail-v">{viewDetail.issueType}</div>
                      </div>
                      {viewDetail.phone && (
                        <div className="col-sm-6 col-lg-4">
                          <div className="sd-admin-detail-k">Phone</div>
                          <div className="sd-admin-detail-v">{viewDetail.phone}</div>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <div className="sd-admin-detail-k">Title</div>
                      <div className="fw-semibold">{viewDetail.issueTitle}</div>
                    </div>
                    <div className="mb-3">
                      <div className="sd-admin-detail-k">Description</div>
                      <div className="text-muted sd-admin-desc small">{viewDetail.issueDescription}</div>
                    </div>
                    {viewDetail.attachment && (
                      <div className="mb-3 pt-2 border-top">
                        <div className="sd-admin-detail-k">Attachment</div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={attachmentLoadingId === viewDetail.id}
                          onClick={() => downloadAttachment(viewDetail.id)}
                        >
                          {attachmentLoadingId === viewDetail.id ? "Downloading…" : (
                            <><i className="bi bi-download me-1" aria-hidden="true" />{viewDetail.attachment.fileName}</>
                          )}
                        </button>
                      </div>
                    )}
                    {viewDetail.statusLog?.length > 0 && (
                      <div className="sd-admin-timeline-wrap">
                        <div className="sd-admin-detail-k mb-2">Activity</div>
                        <ul className="list-unstyled mb-0 small sd-admin-timeline">
                          {[...viewDetail.statusLog].reverse().map((log, i) => (
                            <li key={i} className="sd-admin-timeline-item">
                              <span className="text-muted">{new Date(log.at).toLocaleString()}</span>
                              {" · "}
                              <strong>{formatStatus(log.status)}</strong>
                              {log.note ? ` — ${log.note}` : ""}
                              <span className="text-muted"> ({log.by})</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary" onClick={closeModals}>Close</button>
                <button type="button" className="btn sd-admin-save-btn" onClick={() => { closeModals(); openUpdate(viewModalId); }}>
                  Update status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {updateModalId && updateTicket && (
        <div className="modal fade show d-block sd-admin-modal" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="sd-admin-update-title">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content sd-admin-modal-content border-0 shadow">
              <div className="modal-header sd-admin-modal-header border-0 pb-0">
                <div>
                  <h2 className="modal-title h5 mb-1" id="sd-admin-update-title">Update status</h2>
                  <p className="mb-0 small text-muted font-monospace">{updateTicket.id}</p>
                  <p className="mb-0 small text-truncate mt-1" style={{ maxWidth: 320 }} title={updateTicket.issueTitle}>{updateTicket.issueTitle}</p>
                </div>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeModals} />
              </div>
              <div className="modal-body pt-3">
                <div className="mb-3">
                  <label className="form-label fw-semibold small" htmlFor="sd-admin-status-select">Status</label>
                  <select
                    id="sd-admin-status-select"
                    className="form-select"
                    value={updateDraftStatus}
                    onChange={(e) => setUpdateDraftStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-0">
                  <label className="form-label fw-semibold small" htmlFor="sd-admin-note">Note <span className="text-muted fw-normal">(optional)</span></label>
                  <textarea
                    id="sd-admin-note"
                    className="form-control"
                    rows={4}
                    placeholder="Message to include in the ticket log (and in the customer email if status changes)…"
                    value={updateDraftNote}
                    onChange={(e) => setUpdateDraftNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={closeModals} disabled={updateSubmitting}>Cancel</button>
                <button type="button" className="btn sd-admin-save-btn px-4" onClick={submitModalUpdate} disabled={updateSubmitting || !updateDraftStatus}>
                  {updateSubmitting ? "Submitting…" : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
